import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { QueryState } from "@/shared/ui/QueryState";
import { Button, toast } from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeftIcon } from "lucide-react";
import {
  useDeletePrivateLessonTemplate,
  usePrivateLessonTemplateQuery,
  useUpdatePrivateLessonTemplate,
} from "../api/queries";
import { PrivateLessonTemplateForm } from "../ui/PrivateLessonTemplateForm";

export function PrivateLessonTemplateEditPage() {
  const nav = useNavigate({
    from: "/app/private-lessons/templates/$id/edit",
  });
  const { id } = useParams({
    from: "/app/private-lessons/templates/$id/edit",
  });
  const { data, ...result } = usePrivateLessonTemplateQuery(id);
  const updateTemplate = useUpdatePrivateLessonTemplate(id);
  const deleteTemplate = useDeletePrivateLessonTemplate();

  const template = data;

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            {
              label: "Template ແພັກເກດຮຽນສ່ວນຕົວ",
              to: "/app/private-lessons/templates",
            },
            { label: "ແກ້ໄຂ" },
          ]}
        />
      </Header>

      <Main>
        <div className="flex flex-wrap items-center justify-between space-y-2">
          <div>
            <h2 className="flex items-center font-bold text-2xl tracking-tight">
              ແກ້ໄຂ Private Lesson Template
            </h2>
            <p className="text-muted-foreground">
              ປັບປຸງລາຍລະອຽດຂອງ template ແພັກເກດຮຽນສ່ວນຕົວ.
            </p>
          </div>

          <div className="flex gap-2">
            <Button
              variant="destructive"
              onClick={async () => {
                const ok = await import("@devhop/ui").then((m) =>
                  m.confirm({
                    title: "ລຶບ template ແພັກເກດຮຽນສ່ວນຕົວ",
                    description: "ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບ template ແພັກເກດຮຽນສ່ວນຕົວນີ້?",
                    actionText: "ລຶບ",
                    ActionProps: {
                      variant: "destructive",
                    },
                  }),
                );
                if (!ok) return;

                try {
                  await toast.promise(deleteTemplate.run(id), {
                    loading: "ກຳລັງລຶບ...",
                    success: () => {
                      nav({ to: "/app/private-lessons/templates" });
                      return "ລຶບ template ແພັກເກດຮຽນສ່ວນຕົວສຳເລັດ";
                    },
                    error: "ລຶບ template ແພັກເກດຮຽນສ່ວນຕົວລົ້ມເຫຼວ",
                  });
                } catch (error) {
                  // Error handling is done by toast.promise
                }
              }}
            >
              ລຶບ
            </Button>
            <Button
              variant="outline"
              onClick={() =>
                nav({
                  to: "/app/private-lessons/templates/$id",
                  params: { id },
                })
              }
            >
              <ArrowLeftIcon className="size-4" />
              ກັບຄືນ
            </Button>
          </div>
        </div>

        <QueryState
          result={result}
          title="ກຳລັງໂຫຼດ template ແພັກເກດຮຽນສ່ວນຕົວ"
          description="ກຳລັງດຶງລາຍລະອຽດ"
          variant="fullscreen"
        >
          {!template ? null : (
            <div className="mt-6 rounded-xl border bg-card p-6">
              <PrivateLessonTemplateForm
                initialValues={{
                  name: template.name,
                  packageKind: template.packageKind,
                  totalHours: template.totalHours,
                  validDays: template.validDays,
                  pricePerHour: template.pricePerHour,
                  groupSizeMin: template.groupSizeMin,
                  groupSizeMax: template.groupSizeMax,
                  flatPrice: template.flatPrice,
                  tiers: template.tiers ?? [],
                  defaultLocationKind: template.defaultLocationKind,
                  description: template.description || "",
                  status: template.status,
                }}
                onSubmit={async (vals) => {
                  try {
                    await updateTemplate.mutateAsync(vals);

                    nav({
                      to: "/app/private-lessons/templates/$id",
                      params: { id },
                    });
                  } catch (error) {
                    // Error handling is done by the mutation
                  }
                }}
                submitting={updateTemplate.isPending}
              />
            </div>
          )}
        </QueryState>
      </Main>
    </>
  );
}
